/** The container a video URL will decode as, e.g. 'webm' (query string and hash stripped). */
export const videoFormatFromUrl = (url: string): string =>
  url.split(/[?#]/, 1)[0].split('.').pop()?.toLowerCase() ?? '';

/** True on WebKit — Safari, and every browser on iOS, which are all WebKit under the hood. */
export const isWebKitBrowser = (): boolean => /apple/i.test(navigator.vendor ?? '');

/** The first `.webm` dependency's format, if any — WebKit can't upload webm to a WebGL texture. */
export const unsupportedVideoFormat = (videoDependencies: string[]): string | undefined =>
  videoDependencies.map(videoFormatFromUrl).find((format) => format === 'webm');

/**
 * Resets a file input element's value to allow re-selection of the same file.
 * This is needed because onChange won't fire if the same file is selected again.
 * @param event - The file input change event
 */
export const resetFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
  event.target.value = '';
};

/**
 * Handles file selection and automatically resets the input for re-selection.
 * @param event - The file input change event
 * @param onFileSelected - Callback when a file is selected
 */
export const handleFileInputChange = (
  event: React.ChangeEvent<HTMLInputElement>,
  onFileSelected: (file: File) => void,
) => {
  const file = event.target.files?.[0];
  if (file) {
    onFileSelected(file);
    resetFileInput(event);
  }
};

/**
 * Returns true when the browser can decode the given video File. Performs a hidden
 * `<video>` probe against a temporary blob URL and resolves `true` only when an actual
 * video frame is rendered (`requestVideoFrameCallback`). Resolves `false` on a media
 * error or a timeout with no frame.
 *
 * `loadeddata` / `canplay` aren't reliable signals here: HTMLMediaElement's readyState
 * advances based on whatever track has data at the current playback position. For a
 * `.mov` carrying ProRes video + AAC audio, the audio decodes fine, advances
 * readyState, and fires `loadeddata` — even though the video stream never renders a
 * frame. rVFC is per-track on the video output, so it ignores audio progress.
 *
 * Older browsers without rVFC fall back to `loadeddata`. That's the same audio-track
 * race for any Firefox < 132 / Safari < 15.4 user, but it's the same gap they had
 * before this probe existed and the upload pipeline downstream catches obvious
 * failures.
 */
export function probeVideoFileBrowserSupport(file: File): Promise<boolean> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const probe = document.createElement('video');
    // `auto` so the browser actually fetches enough bytes to decode; `metadata` can
    // settle without ever attempting frame decode.
    probe.preload = 'auto';
    probe.muted = true;

    const finish = (supported: boolean) => {
      clearTimeout(timer);
      probe.removeEventListener('loadeddata', onFallbackLoaded);
      probe.removeEventListener('error', onError);
      probe.pause();
      probe.removeAttribute('src');
      probe.load();
      URL.revokeObjectURL(url);
      resolve(supported);
    };
    const onError = () => finish(false);
    const onFallbackLoaded = () => finish(true); // browsers without rVFC

    probe.addEventListener('error', onError);
    if ('requestVideoFrameCallback' in HTMLVideoElement.prototype) {
      probe.requestVideoFrameCallback(() => finish(true));
    } else {
      probe.addEventListener('loadeddata', onFallbackLoaded);
    }
    probe.src = url;
    // play() drives the decoder; `muted` bypasses autoplay restrictions. The promise
    // can also reject for benign reasons (autoplay policy edge cases), so swallow it
    // — rVFC / error / timeout are the load-bearing signals.
    void probe.play().catch(() => undefined);
    // No frame within 5s and no error → the video track isn't decoding. Typical
    // ProRes-with-audio signature: audio plays, video stalls, no error ever fires.
    // Treat as unsupported — false negatives (rejecting a valid file) are recoverable
    // by retry; false positives (accepting an unplayable file) silently land on canvas
    // as a static frame, which is the user-visible bug we're trying to fix.
    const timer = setTimeout(() => finish(false), 5000);
  });
}
